import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal as AntModal } from 'antd';
import './Modal.scss';

// Create context for sharing modal state with sub-components
const ModalContext = createContext();

// Hook to access modal context
export const useModalContext = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('Modal sub-components must be used within a Modal component');
    }
    return context;
};

/**
 * Design System Modal Component
 *
 * A flexible modal wrapper around Ant Design Modal with standardized styling
 * and support for compound component pattern (Modal.Header, Modal.Body, Modal.Footer)
 *
 * @example
 * <Modal visible={true} onClose={handleClose} width="large">
 *   <Modal.Header title="Edit Sales Order" subtitle="SO-12345" />
 *   <Modal.Body>Content here</Modal.Body>
 *   <Modal.Footer buttons={[...]} />
 * </Modal>
 */
const Modal = ({
    visible,
    onClose,
    width = 'large',
    bodyStyle,
    className = '',
    maskClosable = false,
    centered = false,
    closable = true,
    children,
    ...props
}) => {
    // Size presets
    const sizePresets = {
        small: 600,
        medium: 900,
        large: 1200,
        xl: 1400,
        xxl: 1600,
        wide: 1800
    };

    // Check if fullscreen mode
    const isFullscreen = width === 'fullscreen';

    // Determine width (use preset or custom number)
    const modalWidth = isFullscreen
        ? 'calc(100vw - 48px)'
        : (typeof width === 'string' ? sizePresets[width] : width);

    // Default body style (overflow handled by CSS)
    const defaultBodyStyle = {
        ...bodyStyle,
        ...(isFullscreen && {
            height: 'calc(100vh - 200px)',
            overflow: 'auto'
        })
    };

    // Fullscreen modal styles
    const fullscreenStyle = isFullscreen ? {
        top: 24,
        paddingBottom: 0,
        maxWidth: 'calc(100vw - 48px)'
    } : {};

    // Context value to share with sub-components
    const contextValue = {
        onClose,
        isFullscreen
    };

    return (
        <ModalContext.Provider value={contextValue}>
            <AntModal
                open={visible}
                onCancel={onClose}
                width={modalWidth}
                styles={{ body: defaultBodyStyle }}
                className={`ds-modal ${isFullscreen ? 'ds-modal--fullscreen' : ''} ${className}`}
                mask={{ closable: maskClosable }}
                centered={!isFullscreen}
                footer={null}
                closable={closable}
                style={fullscreenStyle}
                {...props}
            >
                {children}
            </AntModal>
        </ModalContext.Provider>
    );
};

Modal.propTypes = {
    /** Modal visibility */
    visible: PropTypes.bool.isRequired,

    /** Close handler */
    onClose: PropTypes.func.isRequired,

    /** Modal width: preset name or number in pixels */
    width: PropTypes.oneOfType([
        PropTypes.oneOf(['small', 'medium', 'large', 'xl', 'xxl', 'wide', 'fullscreen']),
        PropTypes.number
    ]),

    /** Custom body styles */
    bodyStyle: PropTypes.object,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Allow closing by clicking mask */
    maskClosable: PropTypes.bool,

    /** Show close X button in top-right */
    closable: PropTypes.bool,

    /** Center modal vertically */
    centered: PropTypes.bool,

    /** Modal content */
    children: PropTypes.node
};

export default Modal;
